import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, Tab, Tabs } from "@mui/material";
import {
  ClinicalPageShell,
  SignalBar,
  ClinicalDataGrid,
  StatusPill,
  fmtMoney,
  tokens,
} from "../../shared/clinical-design";
import { CHARGE_CAPTURE_TABS, CHARGE_STATUS, CONFIDENCE } from "../common/constants";
import * as svc from "../services/chargeCaptureService";
import ChargeSlipPanel from "../components/ChargeSlipPanel";
import MissingChargesPanel from "../components/MissingChargesPanel";
import MetricsPanel from "../components/MetricsPanel";

/**
 * Módulo N — Charge Capture & Validation Engine.
 * Cierra el último tramo del ciclo: convierte la documentación clínica en
 * charges correctos y enviados, y caza los encounters sin facturar.
 *
 * Layout master-detail (doc §UI/UX): signal bar arriba, cola de encounters a
 * la izquierda, Charge Slip Workspace a la derecha. Tabs adicionales para el
 * Missing Charges Dashboard (N.3) y las métricas del mes.
 */
export default function ChargeCapturePage() {
  const [tab, setTab] = useState(CHARGE_CAPTURE_TABS.QUEUE);
  const [queue, setQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [loadingCharge, setLoadingCharge] = useState(false);
  const [missingSummary, setMissingSummary] = useState(null);
  const [metrics, setMetrics] = useState(null);

  const loadQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const rows = await svc.getChargeQueue();
      setQueue(rows);
      if (!selectedId && rows.length > 0) setSelectedId(rows[0].chargeId);
    } finally {
      setLoadingQueue(false);
    }
  }, [selectedId]);

  useEffect(() => {
    loadQueue();
    svc.getMissingCharges().then((m) => setMissingSummary(m.summary));
    svc.getMetrics().then(setMetrics);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedId) return;
    setLoadingCharge(true);
    svc
      .getChargeById(selectedId)
      .then(setSelectedCharge)
      .finally(() => setLoadingCharge(false));
  }, [selectedId]);

  const handleChargeUpdated = (updated) => {
    setSelectedCharge(updated);
    setQueue((prev) => prev.map((c) => (c.chargeId === updated.chargeId ? { ...c, status: updated.status } : c)));
  };

  const signals = useMemo(() => {
    const pendingReview = queue.filter((c) => ["RECOMMENDED", "IN_REVIEW"].includes(c.status)).length;
    const syncErrors = queue.filter((c) => c.status === "REJECTED").length;
    return [
      { label: "Charges pending", value: pendingReview, tone: pendingReview > 0 ? "warn" : "ok" },
      {
        label: "Missing charges $",
        value: missingSummary ? fmtMoney(missingSummary.monthlyRevenueAtRisk) : "—",
        tone: "crit",
        hint: "Estimated monthly revenue at risk from unbilled encounters",
      },
      {
        label: "Validation pass",
        value: metrics ? `${metrics.captureRate}%` : "—",
        trend: metrics ? `target ${metrics.captureRateTarget}%` : "",
        tone: metrics && metrics.captureRate >= metrics.captureRateTarget ? "ok" : "warn",
      },
      { label: "Sync errors", value: syncErrors, tone: syncErrors > 0 ? "crit" : "ok" },
      { label: "Revenue impact MTD", value: metrics ? fmtMoney(metrics.revenueImpact) : "—", tone: "ok" },
    ];
  }, [queue, missingSummary, metrics]);

  return (
    <ClinicalPageShell
      title="Charge Capture"
      subtitle="Charge Capture & Validation Engine — bridges what the provider documents and what gets billed: AI-recommended charge slips, pre-submit validation, missing charge detection and AdvancedMD synchronization."
      moduleTag="Module N"
      devPreview
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{
          minHeight: 36,
          mb: 1.5,
          "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: 13.5, color: tokens.ink2 },
          "& .Mui-selected": { color: `${tokens.primary} !important`, fontWeight: 600 },
          "& .MuiTabs-indicator": { bgcolor: tokens.primary },
        }}
      >
        <Tab value={CHARGE_CAPTURE_TABS.QUEUE} label="Charge Queue" />
        <Tab value={CHARGE_CAPTURE_TABS.MISSING} label="Missing Charges" />
        <Tab value={CHARGE_CAPTURE_TABS.METRICS} label="Metrics" />
      </Tabs>

      {tab === CHARGE_CAPTURE_TABS.QUEUE && (
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, lg: 5 }}>
            <ClinicalDataGrid
              loading={loadingQueue}
              rows={queue}
              rowKey={(r) => r.chargeId}
              onRowClick={(r) => setSelectedId(r.chargeId)}
              isRowSelected={(r) => r.chargeId === selectedId}
              maxHeight={720}
              emptyMessage="No encounters waiting for charge review."
              columns={[
                { key: "dateOfService", header: "DOS", width: 100, mono: true },
                {
                  key: "patientName",
                  header: "Patient",
                  render: (r) => (
                    <Box>
                      <Box sx={{ fontSize: 13, fontWeight: 500 }}>{r.patientName}</Box>
                      <Box sx={{ fontSize: 11.5, color: tokens.ink3 }}>{r.visitType} · {r.providerName}</Box>
                    </Box>
                  ),
                },
                {
                  key: "status",
                  header: "Status",
                  width: 130,
                  render: (r) => {
                    const st = CHARGE_STATUS[r.status] || CHARGE_STATUS.DRAFT;
                    return <StatusPill tone={st.tone} label={st.label} dense />;
                  },
                },
                {
                  key: "confidenceOverall",
                  header: "Conf.",
                  align: "right",
                  width: 70,
                  mono: true,
                  render: (r) => `${r.confidenceOverall}%`,
                },
                {
                  key: "estimatedRevenue",
                  header: "Est. $",
                  align: "right",
                  width: 90,
                  mono: true,
                  render: (r) => fmtMoney(r.estimatedRevenue, { maximumFractionDigits: 2 }),
                },
              ]}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 7 }}>
            <ChargeSlipPanel
              charge={selectedCharge}
              loading={loadingCharge}
              onChargeUpdated={handleChargeUpdated}
            />
          </Grid>
        </Grid>
      )}

      {tab === CHARGE_CAPTURE_TABS.MISSING && <MissingChargesPanel />}
      {tab === CHARGE_CAPTURE_TABS.METRICS && <MetricsPanel />}
    </ClinicalPageShell>
  );
}
